/**
 * IconPicker — seleção de qualquer ícone do lucide-react.
 *
 * Uso:
 *   <IconPicker value={iconName} onChange={setIconName} defaultIcon="Wrench" />
 *
 * - `value`: nome do ícone Lucide (ex.: "Wrench", "Car", "Truck")
 * - `onChange(name)`: chamado com o nome ao selecionar
 * - `defaultIcon`: fallback visual quando `value` estiver vazio
 *
 * Dica: `renderLucideIcon(name, props)` também é exportado como helper para
 * renderizar um ícone pelo nome em qualquer outro lugar (ex.: listagens).
 */
import { useMemo, useState } from "react";
import * as LucideIcons from "lucide-react";
import { Search, X, Check } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import { getAllIcons, searchIcons } from "../../utils/lucideCatalog";

export function renderLucideIcon(name, props = {}) {
  if (!name) return null;
  const Comp = LucideIcons[name];
  if (!Comp) return null;
  return <Comp {...props} />;
}

/** Catálogo de ícones em diálogo — use quando o gatilho for outro elemento. */
export function IconCatalogDialog({ open, onOpenChange, value, onChange, contentClassName = "", overlayClassName = "" }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => (open ? searchIcons(q) : []), [q, open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-2xl ${contentClassName}`} overlayClassName={overlayClassName}>
        <DialogHeader>
          <DialogTitle>Escolher ícone</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            placeholder={`Pesquise em ${getAllIcons().length} ícones… (ex.: car, tool, arrow)`}
            className="w-full h-10 pl-9 pr-9 rounded-lg border border-slate-200 bg-slate-50 text-sm focus:outline-none focus:border-cyan-300 focus:ring-2 focus:ring-cyan-100"
            data-testid="icon-picker-search"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="grid grid-cols-8 sm:grid-cols-10 md:grid-cols-12 gap-1 overflow-y-auto max-h-[420px] p-1" data-testid="icon-picker-grid">
          {filtered.length === 0 && (
            <p className="col-span-full text-center text-sm text-slate-400 py-8">Nenhum ícone encontrado.</p>
          )}
          {filtered.map(({ name, Comp }) => {
            const selected = name === value;
            return (
              <button
                key={name}
                type="button"
                onClick={() => onChange && onChange(name)}
                title={name}
                className={`aspect-square rounded-md flex items-center justify-center transition-colors relative ${
                  selected ? "bg-cyan-500 text-white ring-2 ring-cyan-300" : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`icon-option-${name}`}
              >
                <Comp size={18} />
                {selected && <Check size={10} className="absolute top-0.5 right-0.5" />}
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <p className="text-xs text-slate-400">{filtered.length} de {getAllIcons().length} ícones</p>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function IconPicker({
  value,
  onChange,
  defaultIcon = "Wrench",
  label,
  size = 20,
  // permitem subir o catálogo acima de drawers (que ficam em z-index alto)
  contentClassName = "",
  overlayClassName = "",
}) {
  const [open, setOpen] = useState(false);
  const currentName = value || defaultIcon;
  const CurrentComp = LucideIcons[currentName] || LucideIcons[defaultIcon];

  return (
    <>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="h-11 w-11 rounded-lg border-2 border-slate-200 hover:border-cyan-300 hover:bg-cyan-50 flex items-center justify-center text-slate-700 transition-colors"
          data-testid="icon-picker-trigger"
          title="Escolher ícone"
        >
          {CurrentComp ? <CurrentComp size={size} /> : null}
        </button>
        <div className="text-xs text-slate-500">
          {label ? <p className="font-semibold text-slate-700">{label}</p> : null}
          <p>Ícone: <code className="text-slate-700">{currentName}</code></p>
        </div>
      </div>

      <IconCatalogDialog
        open={open}
        onOpenChange={setOpen}
        value={currentName}
        onChange={(nome) => { onChange && onChange(nome); setOpen(false); }}
        contentClassName={contentClassName}
        overlayClassName={overlayClassName}
      />
    </>
  );
}
